import { showLoading, hideLoading } from "../shared/ui/load-modal";
import { reportDataCallFilesUpload } from "../core/api/report-api";
import { Config } from "./config";

type AcceptedExtension = typeof Config.UPLOAD.ACCEPTED_EXTENSIONS[number];

export class FileUploadManager {
  private uploadBtn: HTMLButtonElement | null;
  private fileInput: HTMLInputElement | null;
  private uploadStatus: HTMLParagraphElement | null;
  private dropZone: HTMLDivElement | null;
  private browseBtn: HTMLButtonElement | null;
  private clearFilesBtn: HTMLButtonElement | null;
  private selectedFilesUl: HTMLUListElement | null;
  private incurredCheckbox: HTMLInputElement | null;
  
  private readonly ACCEPTED_EXT = [...Config.UPLOAD.ACCEPTED_EXTENSIONS];
  private selectedFiles: File[] = [];

  constructor() {
    this.uploadBtn = document.getElementById("uploadBtn") as HTMLButtonElement | null;
    this.fileInput = document.getElementById("fileInput") as HTMLInputElement | null;
    this.uploadStatus = document.getElementById("uploadStatus") as HTMLParagraphElement | null;
    this.dropZone = document.getElementById("dropZone") as HTMLDivElement | null;
    this.browseBtn = document.getElementById("browseBtn") as HTMLButtonElement | null;
    this.clearFilesBtn = document.getElementById("clearFilesBtn") as HTMLButtonElement | null;
    this.selectedFilesUl = document.getElementById("selectedFiles") as HTMLUListElement | null;
    this.incurredCheckbox = document.getElementById("incurredCheckbox") as HTMLInputElement | null;
  }

  public init() {
    this.wireEvents();
    this.renderFileList();
    
    // Listen for clearFileQueue event triggered by successful upload
    document.addEventListener('clearFileQueue', () => {
      this.clearFileQueue();
    });
  }

  private getExt = (name: string): AcceptedExtension => name.slice(name.lastIndexOf(".")).toLowerCase() as AcceptedExtension;
  private isAllowed = (file: File) => this.ACCEPTED_EXT.includes(this.getExt(file.name));
  private makeKey = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;

  private clearFileQueue = () => {
    this.selectedFiles.splice(0, this.selectedFiles.length);
    this.renderFileList();
    if (this.uploadStatus) this.uploadStatus.textContent = "";
    // Clear the file input value to allow browse button to work properly
    if (this.fileInput) this.fileInput.value = "";
  };

  private renderFileList = () => {
    if (!this.selectedFilesUl) return;
    this.selectedFilesUl.innerHTML = "";
    if (!this.selectedFiles.length) {
      const li = document.createElement("li");
      li.textContent = "No files selected.";
      this.selectedFilesUl.appendChild(li);
      return;
    }
    this.selectedFiles.forEach((f, i) => {
      const li = document.createElement("li");
      const left = document.createElement("span");
      left.textContent = `${f.name} (${(f.size / 1024).toFixed(1)} KB)`;

      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "remove-file";
      removeBtn.setAttribute("aria-label", `Remove ${f.name}`);
      removeBtn.textContent = "Remove";
      removeBtn.addEventListener("click", () => {
        this.selectedFiles.splice(i, 1);
        this.renderFileList();
      });

      li.append(left, removeBtn);
      this.selectedFilesUl.appendChild(li);
    });
  };

  private addFiles = (incoming: File[]) => {
    const before = this.selectedFiles.length;
    for (const f of incoming) {
      if (!this.isAllowed(f)) continue;
      const key = this.makeKey(f);
      if (!this.selectedFiles.some((sf) => this.makeKey(sf) === key)) this.selectedFiles.push(f);
    }
    this.renderFileList();
    if (this.selectedFiles.length === before && this.uploadStatus) {
      this.uploadStatus.textContent = "No new files added (duplicates or unsupported types).";
    }
  };

  private handleFiles = (list?: FileList | File[] | null) => {
    if (!list) return;
    const arr = Array.from(list);
    const accepted = arr.filter(this.isAllowed);
    const rejected = arr.filter((f) => !this.isAllowed(f));
    this.addFiles(accepted);
    if (rejected.length && this.uploadStatus) {
      this.uploadStatus.textContent = `Some files were ignored due to type: ${rejected
        .map((r) => r.name)
        .join(", ")}`;
    }
  };

  private wireEvents() {
    this.browseBtn?.addEventListener("click", () => this.fileInput?.click());
    this.clearFilesBtn?.addEventListener("click", () => {
      this.selectedFiles.splice(0, this.selectedFiles.length);
      this.renderFileList();
      if (this.uploadStatus) this.uploadStatus.textContent = "";
      // Clear the file input value to allow browse button to work properly
      if (this.fileInput) this.fileInput.value = "";
    });

    this.dropZone?.addEventListener("click", () => this.fileInput?.click());
    this.dropZone?.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.fileInput?.click();
      }
    });

    const highlight = () => this.dropZone?.classList.add("drop-zone--over");
    const unhighlight = () => this.dropZone?.classList.remove("drop-zone--over");

    ["dragenter", "dragover"].forEach((evt) =>
      this.dropZone?.addEventListener(evt, (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        highlight();
      })
    );

    ["dragleave", "dragend", "drop"].forEach((evt) =>
      this.dropZone?.addEventListener(evt, (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        unhighlight();
      })
    );

    this.dropZone?.addEventListener("drop", (e: DragEvent) => {
      const dt = e.dataTransfer;
      if (dt?.files?.length) this.handleFiles(dt.files);
    });

    this.fileInput?.addEventListener("change", () => this.handleFiles(this.fileInput.files));

    this.uploadBtn?.addEventListener("click", async () => {
      await this.handleUpload();
    });
  }

  private async handleUpload() {
    const uploadDataSelect = document.getElementById("uploadDataSelect") as HTMLSelectElement | null;
    const uploadCarrierSelect = document.getElementById("uploadCarrierSelect") as HTMLSelectElement | null;
    const uploadClientSelect = document.getElementById("uploadClientSelect") as HTMLSelectElement | null;

    const dataType = uploadDataSelect ? Number(uploadDataSelect.value) : 0;
    const uploadCarrier = uploadCarrierSelect ? String(uploadCarrierSelect.value) : "";
    const uploadClient = uploadClientSelect ? String(uploadClientSelect.value) : "";
    const isIncurred = this.incurredCheckbox ? this.incurredCheckbox.checked : false;

    // Basic validation
    if (!this.selectedFiles.length) {
      this.uploadStatus && (this.uploadStatus.textContent = "Please add at least one file.");
      return;
    }
    if (!dataType) {
      this.uploadStatus && (this.uploadStatus.textContent = "Please select a data type.");
      return;
    }
    if (!uploadCarrier || uploadCarrier === "0") {
      this.uploadStatus && (this.uploadStatus.textContent = "Please select a carrier.");
      return;
    }
    if (!uploadClient || uploadClient === "0") {
      this.uploadStatus && (this.uploadStatus.textContent = "Please select a client.");
      return;
    }

    try {
      showLoading({text : "Uploading File(s)..."});
      await reportDataCallFilesUpload(dataType, uploadCarrier, uploadClient, this.selectedFiles, isIncurred);
      this.uploadStatus && (this.uploadStatus.textContent = "Upload complete.");
    } catch (err: any) {
      this.uploadStatus && (this.uploadStatus.textContent = `Upload failed: ${err?.message ?? err}`);
    } finally {
      hideLoading();
    }
  }
}
